import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { NotificationLayerProvider } from "by/gamefactory/cityversetycoon/composition/providers/NotificationLayerProvider";
import { BuildingRightsType } from "by/gamefactory/cityversetycoon/networking/data/responses/user/BuildingRightsType";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { IDestroyOptions, Point, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { GameFont } from "../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { UnlockPiggyAnimationView } from "../../../common/components/animations/UnlockPiggyAnimationView";
import { IBuildingModel } from "../../../common/models/Building/IBuildingModel";
import { FixResult } from "../../../common/models/Building/upgrade/FixResult";
import { UpgradeResult } from "../../../common/models/Building/upgrade/UpgradeResult";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { IGameViewModel } from "../../../game/models/IGameViewModel";
import { IFocusable } from "../../../layers/IFocusable";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { BuildingUpgradeItemLandlordRewardComponent } from "../components/BuildingUpgradeItemLandlordRewardComponent";
import { BuildingUpgradeItemRenterRewardComponent } from "../components/BuildingUpgradeItemRenterRewardComponent";
import { InitialRewardComponent } from "../components/InitialRewardComponent";
import { PiggyAnimationPromiseHolder } from "../components/PiggyAnimationPromiseHolder";
import { RewardContainer } from "../components/RewardContainer";
import { IUpgradeItemSceneController } from "../controller/IUpgradeItemSceneController";
import { BuildingUpgradeItemResourcePackage } from "../resources/BuildingUpgradeItemResourcePackage";

export class BuildingUpgradeItemPopupView extends BaseScalableView<void> implements IFocusable {
    public readonly focusable: boolean = true;

    private static readonly ITEM_UPGRADED_TEXT = 'ITEM\nUPGRADED';
    private static readonly BUTTON_TEXT = 'CLAIM REWARD';
    private static readonly OK_BUTTON_TEXT = 'OK';
    private static readonly PORTION_TEXT = 'Portion of your rewards will be shared\nwith your landlord since he owns your building';
    private static readonly KEEP_TEXT = 'Keep upgrading to earn OWN Points and other rewards\nto become an owner and earn all OWN Points for yourself!';

    private readonly commonResources: CommonResourcesPackage;
    private readonly upgradeItemResources: BuildingUpgradeItemResourcePackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly role: BuildingRightsType;
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;
    private readonly buildingModel: IBuildingModel;
    private readonly upgradeItemSceneController: IUpgradeItemSceneController;
    private readonly landlordAvatar: Texture;
    private readonly upgradeOrFixResult: FixResult | UpgradeResult;
    private readonly notificationLayerProvider: NotificationLayerProvider;
    private readonly gameViewModel: IGameViewModel;
    private readonly topPanel: TopPanel;
    private readonly piggyAnimationPcs: PiggyAnimationPromiseHolder;

    private fadeScreen: FadeScreen;
    private blueTopLight: Sprite;
    private tokens: Sprite;
    private leftBlueLight: Sprite;
    private rightBlueLight: Sprite;
    private bottomYellowLight: Sprite;
    private claimButton: ButtonWithScaleAnimation;
    private itemUpgradedText: Text;
    private renterRewardComponent: BuildingUpgradeItemRenterRewardComponent;
    private landlordRewardComponent: BuildingUpgradeItemLandlordRewardComponent;
    private portionText: Text;
    private keepText: Text;
    private animation: gsap.core.Timeline;
    private claimAnimation: gsap.core.Timeline;
    private rewardContainer: RewardContainer;
    private flag: boolean;
    private initialRewardComponent: InitialRewardComponent;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        upgradeItemResources: BuildingUpgradeItemResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        guestModel: IGuestModel,
        playerModel: IPlayerModel,
        upgradeItemSceneController: IUpgradeItemSceneController,
        landlordAvatar: Texture,
        buildingModel: IBuildingModel,
        notificationLayerProvider: NotificationLayerProvider,
        gameViewModel: IGameViewModel,
        topPanel: TopPanel,
        piggyAnimationPcs: PiggyAnimationPromiseHolder
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.upgradeItemResources = upgradeItemResources;
        this.userProfileResources = userProfileResources;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.role = playerModel.buildingRights.value;
        this.landlordAvatar = landlordAvatar;
        this.buildingModel = buildingModel;
        this.upgradeOrFixResult = buildingModel.upgradeOrFixResult;
        this.upgradeItemSceneController = upgradeItemSceneController;
        this.notificationLayerProvider = notificationLayerProvider;
        this.gameViewModel = gameViewModel;
        this.topPanel = topPanel;
        this.piggyAnimationPcs = piggyAnimationPcs;
        this.animation = new gsap.core.Timeline();
        this.claimAnimation = new gsap.core.Timeline();
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.claimButton.onUp.add(this.claimButtonClickHandle, this);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.3, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";

        this.blueTopLight = new Sprite(this.upgradeItemResources.assets_views_building_upgrade_item_blue_light_top_png);
        this.tokens = new Sprite(this.upgradeItemResources.assets_views_building_upgrade_item_tokens_png);
        this.leftBlueLight = new Sprite(this.upgradeItemResources.assets_views_building_upgrade_item_blue_light_vertical_png);
        this.rightBlueLight = new Sprite(this.upgradeItemResources.assets_views_building_upgrade_item_blue_light_vertical_png);
        this.rightBlueLight.scale.x = -1;
        this.bottomYellowLight = new Sprite(this.upgradeItemResources.assets_views_building_upgrade_item_bottom_yellow_light_png);
        this.itemUpgradedText = new Text(BuildingUpgradeItemPopupView.ITEM_UPGRADED_TEXT, new ItemUpgradedTextStyle());
        this.itemUpgradedText.skew.x = -0.09;
        this.itemUpgradedText.angle = -5;
        this.claimButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        const buttonText = new Text(this.buildingModel.fromUpgrade ? BuildingUpgradeItemPopupView.BUTTON_TEXT : BuildingUpgradeItemPopupView.OK_BUTTON_TEXT, new ButtonTextStyle());
        buttonText.position.set(this.claimButton.width * 0.5 - buttonText.width * 0.5, this.claimButton.height * 0.5 - buttonText.height * 0.5);
        this.claimButton.defaultView.addChild(buttonText);

        this.addChild(
            this.fadeScreen,
            this.blueTopLight,
            this.leftBlueLight,
            this.rightBlueLight,
            this.bottomYellowLight,
            this.tokens,
            this.claimButton,
            this.itemUpgradedText
        );
        this.rewardContainer = new RewardContainer(this.upgradeOrFixResult.totalRewardInTokens, this.upgradeItemResources.assets_views_building_upgrade_item_token_png);
        this.addChild(this.rewardContainer);
        switch (this.role) {
            case BuildingRightsType.OWNER:
            case BuildingRightsType.LANDLORD: {
                this.landlordRewardComponent = new BuildingUpgradeItemLandlordRewardComponent(
                    this.upgradeItemResources,
                    this.upgradeOrFixResult.currentGuestRewardInTokens
                );
                this.addChild(this.landlordRewardComponent);
                this.rewardContainer.visible = false;
                this.initialRewardComponent = new InitialRewardComponent(this.upgradeItemResources, this.commonResources, this.upgradeOrFixResult);
                this.addChild(this.initialRewardComponent);
                this.initialRewardComponent.visible = this.upgradeOrFixResult.piggyMultiplier != 1 || this.upgradeOrFixResult.initialRewardInTokens != this.upgradeOrFixResult.totalRewardInTokens;
                break;
            }
            case BuildingRightsType.RENTER: {
                this.renterRewardComponent = new BuildingUpgradeItemRenterRewardComponent(
                    this.commonResources,
                    this.upgradeItemResources,
                    this.userProfileResources,
                    this.guestModel.avatarTexture.value,
                    this.landlordAvatar,
                    this.upgradeOrFixResult,
                    this.playerModel,
                    this.canvasScaler
                );
                if (!this.buildingModel.fromUpgrade) {
                    this.rewardContainer.visible = false;
                }
                this.renterRewardComponent.onLandlordInfoButtonClick.add(this.landlordInfoButtonClickHandle, this);
                this.renterRewardComponent.onRenterInfoButtonClick.add(this.renterInfoButtonClickHandle, this);
                this.renterRewardComponent.visible = !this.buildingModel.fromUpgrade;

                this.portionText = new Text(BuildingUpgradeItemPopupView.PORTION_TEXT, new RenterDescriptionTextStyle());
                this.keepText = new Text(BuildingUpgradeItemPopupView.KEEP_TEXT, new RenterBottomDescriptionTextStyle());

                this.portionText.visible = !this.buildingModel.fromUpgrade;
                this.keepText.visible = !this.buildingModel.fromUpgrade;
                this.addChild(
                    this.renterRewardComponent,
                    this.portionText,
                    this.keepText
                );
                break;
            }
        }
    }

    private layout(width: number, height: number): void {
        this.claimButton.position.set(229, this.role === BuildingRightsType.RENTER ? 1379 : 1445.5);
        this.bottomYellowLight.position.set(57, 1569);
        this.leftBlueLight.position.set(0, 443);
        this.rightBlueLight.position.set(width, 443);
        this.rewardContainer.position.set(129, 628);
        switch (this.role) {
            case BuildingRightsType.OWNER:
            case BuildingRightsType.LANDLORD: {
                this.itemUpgradedText.position.set(191, 272);
                this.landlordRewardComponent.position.set(129, 698);
                this.initialRewardComponent.position.set(43, 1058);
                break;
            }
            case BuildingRightsType.RENTER: {
                this.itemUpgradedText.position.set(191, 279);
                this.renterRewardComponent.position.set(194, 799);
                this.portionText.position.set(97, 626);
                this.keepText.position.set(57, 1597);
                break;
            }
        }
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
        this.animation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.3, ease: "power1.in" });
        if (this.role != BuildingRightsType.RENTER) {
            this.animation.fromTo(this.initialRewardComponent, { alpha: 0 }, { alpha: 1, duration: 0.5 }, ">");
        }
        if (!(this.buildingModel.fromUpgrade || this.role === BuildingRightsType.OWNER)) {
            this.animation.add(() => {
                if (this.role === BuildingRightsType.RENTER) {
                    this.renterRewardComponent.playAvatarsAnimations();
                }
            });
        }
        await this.animation.play();
        this.animation.clear();
    }

    private async hide(): Promise<void> {
        this.animation.to(this, { alpha: 0, duration: 0.5, delay: 0.5, ease: "power1.out" });
        await this.animation.play();
        this.animation.clear();
    }

    private async claimButtonClickHandle(): Promise<void> {
        this.claimButton.enabled = false;
        if (!this.buildingModel.fromUpgrade) {
            this.hideShaders();
            await this.hide();
            this.operationControls.complete();
            return;
        }
        if (!this.flag && this.role === BuildingRightsType.RENTER) {
            this.claimAnimation.fromTo(this.rewardContainer, { alpha: 1 }, {
                alpha: 0, duration: 0.5, onComplete: () => {
                    this.renterRewardComponent.visible = true;
                }
            });
            this.claimAnimation.fromTo(this.initialRewardComponent, { alpha: 1 }, { alpha: 0, duration: 0.5 }, "<");
            this.claimAnimation.fromTo(this.renterRewardComponent, { alpha: 0 }, { alpha: 1, duration: 0.5 }, ">");
            this.portionText.visible = true;
            this.keepText.visible = true;
            this.claimAnimation.fromTo(this.portionText, { alpha: 0 }, {
                alpha: 1,
                duration: 0.5,
                onStart: () => {
                }
            }, ">");
            this.claimAnimation.fromTo(this.keepText, { alpha: 0 }, {
                alpha: 1,
                duration: 0.5,
                onComplete: () => {
                    this.renterRewardComponent.playAvatarsAnimations();
                    this.claimButton.enabled = true;
                }
            }, "<");
            await this.claimAnimation.play();
            this.claimAnimation?.kill();
            this.flag = true;
            return;
        }
        this.hideShaders();
        if (this.buildingModel.fromUpgrade) {
            const loyaltyCoinsFlyAnimation = new UnlockPiggyAnimationView(this.commonResources, 12, new Point(-this.width * 0.5 + 50, - this.height * 0.5 + 280 + this.topPanel.y), this.gameViewModel);
            loyaltyCoinsFlyAnimation.position.set(this.width * 0.5 + 70, this.height * 0.5 - 50);
            this.notificationLayerProvider.notificationLayer.addChild(loyaltyCoinsFlyAnimation);
            loyaltyCoinsFlyAnimation.startAnimation();
            this.piggyAnimationPcs.start();
        }
        await this.hide();
        this.operationControls.complete();
    }

    private hideShaders(): void {
        if (this.role === BuildingRightsType.RENTER) {
            this.renterRewardComponent.hide();
        }
    }

    private async landlordInfoButtonClickHandle(): Promise<void> {
        this.keepText.visible = false;
        await this.upgradeItemSceneController.landlordRenterInfoPopup();
        this.keepText.visible = true;
    }

    private async renterInfoButtonClickHandle(): Promise<void> {
        this.keepText.visible = false;
        await this.upgradeItemSceneController.landlordRenterInfoPopup();
        this.keepText.visible = true;
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.renterRewardComponent?.onLandlordInfoButtonClick.remove(this.landlordInfoButtonClickHandle, this);
        this.renterRewardComponent?.onRenterInfoButtonClick.remove(this.renterInfoButtonClickHandle, this);
        this.claimButton.onUp.remove(this.claimButtonClickHandle, this);
        this.animation?.kill();
        this.claimAnimation?.kill();
        super.destroy(options);
    }
}

class ItemUpgradedTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#4efb5f",
                "#37cb98",
                "#188de2"
            ],
            fillGradientType: 1,
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 142,
            lineHeight: 122,
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 62,
            miterLimit: 0,
            dropShadow: true,
            dropShadowAngle: 0.7,
            dropShadowBlur: 5,
            dropShadowDistance: 3,
            dropShadowAlpha: 0.4
        }));
    }
}

class RenterDescriptionTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.Bebasneuepro_Smerg,
            fontSize: 40,
            align: 'center',
            fill: '#FFFFFF'
        });
    }
}

class RenterBottomDescriptionTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.Bebasneuepro_Smerg,
            fontSize: 36,
            align: 'left',
            fill: '#FFFFFF'
        });
    }
}